import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../api';
import { usePermissions } from '../../hooks/usePermissions';
import { Layout } from '../../components/layout/Layout';
import { AssignedUserSelect } from '../../components/AssignedUserSelect';
import { 
  Smartphone, ArrowLeft, Edit, Trash2,
  Phone, User, Building2, MapPin, Calendar, Tag, 
  Hash, FileText, Package, Mail, Briefcase
} from 'lucide-react';

const MobileDeviceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canUpdate, canDelete } = usePermissions('micro', 'mobile_devices');
  
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOptions, setFormOptions] = useState({});
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchDevice();
    fetchFormOptions();
  }, [id]);

  const fetchDevice = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/assets/mobile-devices/${id}`);
      setDevice(response.data);
    } catch (error) {
      console.error('Error fetching device:', error);
      toast.error('Error al cargar el celular');
      navigate('/micro/mobile-devices');
    } finally {
      setLoading(false);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/mobile-devices/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  const openEditModal = () => {
    setFormData({
      brand_id: device.brand_id || '',
      model: device.model || '',
      os_id: device.os_id || '',
      imei: device.imei || '',
      serial_number: device.serial_number || '',
      phone_number: device.phone_number || '',
      status_id: device.status_id || '',
      assigned_user_id: device.assigned_user_id || '',
      empresa_id: device.empresa_id || '',
      site_id: device.site_id || '',
      location_id: device.location_id || '',
      supplier_id: device.supplier_id || '',
      purchase_date: device.purchase_date || '',
      comments: device.comments || ''
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/assets/mobile-devices/${id}`, formData);
      toast.success('Celular actualizado exitosamente');
      setShowEditModal(false);
      fetchDevice();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleUserCreated = (createdUser) => {
    setFormOptions(prev => ({
      ...prev,
      usuarios: [...(prev.usuarios || []), createdUser]
        .sort((a, b) => (a.name || a.nombre || '').localeCompare(b.name || b.nombre || '', 'es')),
    }));
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/mobile-devices/${id}`);
      toast.success('Celular eliminado exitosamente');
      navigate('/micro/mobile-devices');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return <span className="badge badge-default">Sin estado</span>;
    if (status.toLowerCase().includes('inactivo')) return <span className="badge badge-red">{status}</span>;
    if (status.toLowerCase().includes('activo')) return <span className="badge badge-green">{status}</span>;
    return <span className="badge badge-default">{status}</span>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </Layout>
    );
  }

  if (!device) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Celular no encontrado</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/micro/mobile-devices')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" strokeWidth={1.5} />
            </Button>
            <div className="flex items-center gap-3">
              <Smartphone className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <div>
                <h1 className="text-2xl font-semibold">{device.brand_name} {device.model}</h1>
                <p className="text-sm text-muted-foreground font-mono">{device.imei}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canUpdate && (
              <Button variant="outline" onClick={openEditModal} data-testid="edit-btn">
                <Edit className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Editar
              </Button>
            )}
            {canDelete && (
              <Button 
                variant="destructive" 
                onClick={() => setShowDeleteModal(true)}
                data-testid="delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Eliminar
              </Button>
            )}
          </div>
        </div>

        {/* Summary Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="nerve-card p-4 text-center">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Estado</div>
            <div>{getStatusBadge(device.status_name)}</div>
          </div>
          <div className="nerve-card p-4 text-center">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Marca</div>
            <div className="font-medium">{device.brand_name || '-'}</div>
          </div>
          <div className="nerve-card p-4 text-center">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">SO</div>
            <div className="font-medium text-sm">{device.os_name?.trim() || '-'}</div>
          </div>
          <div className="nerve-card p-4 text-center">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Teléfono</div>
            <div className="font-mono text-sm">{device.phone_number || '-'}</div>
          </div>
          <div className="nerve-card p-4 text-center">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Site</div>
            <div className="font-medium">{device.site_name || '-'}</div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-2 gap-6">
          {/* Identificación */}
          <Card className="card-subtle">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Tag className="h-4 w-4 text-primary" strokeWidth={1.5} />
                Identificación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">IMEI</p>
                  <p className="font-mono">{device.imei}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Serial</p>
                  <p className="font-mono">{device.serial_number || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Marca</p>
                  <p>{device.brand_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Modelo</p>
                  <p>{device.model}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Sistema Operativo</p>
                  <p>{device.os_name?.trim() || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Teléfono</p>
                  <p className="font-mono">{device.phone_number || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Usuario Asignado */}
          <Card className="card-subtle">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4 text-primary" strokeWidth={1.5} />
                Usuario Asignado
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {device.user_name ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-6 w-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-medium">{device.user_name}</p>
                      {device.user_title && (
                        <p className="text-sm text-muted-foreground">{device.user_title}</p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                      <div>
                        <p className="text-xs text-muted-foreground">Departamento</p>
                        <p className="text-sm">{device.user_department || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                      <div>
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="text-sm truncate">{device.user_email || '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <User className="h-8 w-8 mx-auto mb-2 opacity-50" strokeWidth={1.5} />
                  <p>Sin usuario asignado</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Ubicación y Organización */}
          <Card className="card-subtle">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" strokeWidth={1.5} />
                Ubicación y Organización
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Empresa</p>
                  <p>{device.empresa_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Site</p>
                  <p>{device.site_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Ubicación</p>
                  <p>{device.location_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Proveedor</p>
                  <p>{device.supplier_name || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Compra y Notas */}
          <Card className="card-subtle">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" strokeWidth={1.5} />
                Compra y Notas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Fecha de Compra</p>
                  <p>{device.purchase_date || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Comentarios</p>
                  <p className="text-sm">{device.comments || 'Sin comentarios'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Edit Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5" strokeWidth={1.5} />
                Editar Celular
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>IMEI *</Label>
                <Input
                  value={formData.imei || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, imei: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Número de serie (opcional)</Label>
                <Input
                  value={formData.serial_number || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Marca</Label>
                <Select value={String(formData.brand_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, brand_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.brands?.map(b => (
                      <SelectItem key={b.id} value={String(b.id)}>{b.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Modelo *</Label>
                <Input
                  value={formData.model || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Sistema Operativo</Label>
                <Select value={String(formData.os_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, os_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.os?.map(o => (
                      <SelectItem key={o.id} value={String(o.id)}>{o.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={formData.phone_number || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={String(formData.status_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, status_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.statuses?.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <AssignedUserSelect
                value={formData.assigned_user_id}
                onValueChange={(assigned_user_id) => setFormData(prev => ({ ...prev, assigned_user_id }))}
                users={formOptions.usuarios}
                onUserCreated={handleUserCreated}
              />
              
              <div className="space-y-2">
                <Label>Empresa</Label>
                <Select value={String(formData.empresa_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, empresa_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.empresas?.map(e => (
                      <SelectItem key={e.id} value={String(e.id)}>{e.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Site</Label>
                <Select value={String(formData.site_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.sites?.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Ubicación</Label>
                <Select value={String(formData.location_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, location_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.ubicaciones?.map(u => (
                      <SelectItem key={u.id} value={String(u.id)}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Proveedor</Label>
                <Select value={String(formData.supplier_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, supplier_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.proveedores?.map(p => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Fecha de Compra</Label>
                <Input
                  type="date"
                  value={formData.purchase_date || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, purchase_date: e.target.value }))}
                />
              </div>
              
              <div className="col-span-2 space-y-2">
                <Label>Comentarios</Label>
                <Input
                  value={formData.comments || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleUpdate}>
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" strokeWidth={1.5} />
                Eliminar Celular
              </DialogTitle>
            </DialogHeader>
            <p className="py-4">
              ¿Estás seguro de que deseas eliminar el celular <strong>{device?.brand_name} {device?.model}</strong> con IMEI <strong>{device?.imei}</strong>?
              <br />
              <span className="text-sm text-muted-foreground">Esta acción no se puede deshacer.</span>
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MobileDeviceDetail;
